import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "./auth/supabase";
import { useAuth } from "./auth/AuthContext";
import { Footer } from "./App";

/* ============================================================
   History — every check the logged-in user has saved.
   Reads from public.saved_checks. RLS guarantees you only
   see your own rows; no server endpoint needed.
   ============================================================ */

const VERDICT_FROM_TRUTH = {
  true:       { key: "fact",  label: "Fact" },
  false:      { key: "not",   label: "Not"  },
  ambivalent: { key: "mixed", label: "Mixed" },
};

const prettyCategory = (cat) => {
  if (!cat) return "Other";
  const c = String(cat).toLowerCase();
  switch (c) {
    case "health": return "Health";
    case "science": return "Science";
    case "politics": return "Politics";
    case "economy":
    case "economics": return "Economy";
    case "climate": return "Climate / environment";
    case "technology": return "Technology";
    default: return "Other";
  }
};

export default function History() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);   // null = loading, [] = empty, [...]=data
  const [filter, setFilter] = useState("all"); // all | fact | mixed | not
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("saved_checks")
        .select("*")
        .order("created_at", { ascending: false });
      if (cancelled) return;
      if (error) {
        setError(error.message);
        setRows([]);
      } else {
        setRows(data || []);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    if (filter === "all") return rows;
    return rows.filter((r) => {
      const v = VERDICT_FROM_TRUTH[String(r.truth).toLowerCase()];
      return v && v.key === filter;
    });
  }, [rows, filter]);

  const counts = useMemo(() => {
    const c = { all: rows?.length || 0, fact: 0, mixed: 0, not: 0 };
    (rows || []).forEach((r) => {
      const v = VERDICT_FROM_TRUTH[String(r.truth).toLowerCase()];
      if (v) c[v.key]++;
    });
    return c;
  }, [rows]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this saved check?")) return;
    setDeletingId(id);
    const { error } = await supabase.from("saved_checks").delete().eq("id", id);
    setDeletingId(null);
    if (error) {
      alert("Could not delete: " + error.message);
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <main>
      <section className="about-hero">
        <span className="mono-label">Signed in as {user?.email}</span>
        <h1 style={{ fontSize: "clamp(48px, 6vw, 88px)" }}>
          Your saved <em>checks.</em>
        </h1>
        <p className="lead">
          Every claim you&rsquo;ve looked up, stored privately to your account.
        </p>
      </section>

      <section className="history-wrap">
        <div className="history-filters">
          <button
            type="button"
            className={"history-filter" + (filter === "all" ? " active" : "")}
            onClick={() => setFilter("all")}
          >
            All <span className="count">{counts.all}</span>
          </button>
          <button
            type="button"
            className={"history-filter" + (filter === "fact" ? " active" : "")}
            onClick={() => setFilter("fact")}
          >
            <span className="pip pip-fact" /> Fact <span className="count">{counts.fact}</span>
          </button>
          <button
            type="button"
            className={"history-filter" + (filter === "mixed" ? " active" : "")}
            onClick={() => setFilter("mixed")}
          >
            <span className="pip pip-mixed" /> Mixed <span className="count">{counts.mixed}</span>
          </button>
          <button
            type="button"
            className={"history-filter" + (filter === "not" ? " active" : "")}
            onClick={() => setFilter("not")}
          >
            <span className="pip pip-not" /> Not <span className="count">{counts.not}</span>
          </button>
        </div>

        {error && <div className="auth-msg error" role="alert">{error}</div>}

        {filtered === null && (
          <div className="history-empty">
            <p style={{ color: "var(--ink-3)" }}>Loading your checks…</p>
          </div>
        )}

        {filtered && filtered.length === 0 && (
          <div className="history-empty">
            <h3>Nothing saved yet.</h3>
            <p>Run a check on the <Link to="/">home page</Link> and it will land here automatically.</p>
          </div>
        )}

        {filtered && filtered.length > 0 && (
          <ul className="history-list">
            {filtered.map((r) => {
              const v = VERDICT_FROM_TRUTH[String(r.truth).toLowerCase()] || {
                key: "mixed",
                label: "Result",
              };
              const date = new Date(r.created_at);
              const dateStr = date.toLocaleDateString(undefined, {
                year: "numeric", month: "short", day: "numeric",
              });
              return (
                <li key={r.id} className="history-item">
                  <div className="history-item-top">
                    <span className={`verdict ${v.key}`}><span className="pip" />{v.label}</span>
                    <span className="category-pill">{prettyCategory(r.category)}</span>
                    <span className="history-date">{dateStr}</span>
                    <button
                      type="button"
                      className="history-delete"
                      onClick={() => handleDelete(r.id)}
                      disabled={deletingId === r.id}
                      aria-label="Delete this check"
                    >
                      {deletingId === r.id ? "…" : "Delete"}
                    </button>
                  </div>
                  <p className="history-claim">&ldquo;{r.claim}&rdquo;</p>
                  {r.explanation && <p className="history-explanation">{r.explanation}</p>}
                  {(r.source1 || r.source2) && (
                    <div className="sources" style={{ borderTop: 0, paddingTop: 0, marginTop: 12 }}>
                      {[r.source1, r.source2].filter(Boolean).map((href, i) => (
                        <a key={i} href={href} target="_blank" rel="noreferrer" className="source">
                          <span className="num">{String(i + 1).padStart(2, "0")}</span>
                          Source {i + 1}
                        </a>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <Footer />
    </main>
  );
}
